import { useCallback, useEffect, useRef, useState } from "react";

import { obterFrota } from "../api/client";
import type { FleetState } from "../types";

const INTERVALO_MS = 10_000;

interface Estado {
  frota: FleetState | null;
  erro: string | null;
  aRecarregar: boolean;
}

/**
 * Estado da frota, atualizado de 10 em 10 s.
 *
 * Polling e não WebSocket: os lotes chegam a cada 5 s, portanto 10 s de atraso
 * não perde nada de útil, e evita mais uma peça de infraestrutura na VM.
 *
 * Em erro **guarda os dados anteriores**. Uma falha de rede não deve apagar o
 * mapa — mostra-se o último estado conhecido com o erro por cima, que é mais
 * útil do que um ecrã vazio.
 */
export function useFleet(): Estado & { recarregar: () => void } {
  const [estado, setEstado] = useState<Estado>({
    frota: null,
    erro: null,
    aRecarregar: true,
  });
  const abortar = useRef<AbortController | null>(null);

  const buscar = useCallback(async () => {
    abortar.current?.abort();
    const controlo = new AbortController();
    abortar.current = controlo;

    setEstado((a) => ({ ...a, aRecarregar: true }));
    try {
      const frota = await obterFrota(controlo.signal);
      setEstado({ frota, erro: null, aRecarregar: false });
    } catch (erro) {
      if (controlo.signal.aborted) return;
      setEstado((a) => ({
        ...a,
        erro: erro instanceof Error ? erro.message : "Falha a contactar a API",
        aRecarregar: false,
      }));
    }
  }, []);

  useEffect(() => {
    void buscar();
    const t = setInterval(() => void buscar(), INTERVALO_MS);
    return () => {
      clearInterval(t);
      abortar.current?.abort();
    };
  }, [buscar]);

  return { ...estado, recarregar: () => void buscar() };
}
